import { ApiService } from '@supply-mover-2-0/core';
import type { SoftphoneFallbackKindModel } from '@supply-mover-2-0/shared';

/** The missed-call handling kinds a rep can set on their OWN line. */
export type SelfServiceFallbackKind = 'none' | 'voicemail' | 'aiAgent';

/**
 * One number assignment owned by the calling user, with the
 * fields they're allowed to manage themselves: voicemail greeting
 * URL + default-outbound flag (DD.9.1) + missed-call handling.
 */
export interface MyNumberSummaryModel {
	softphoneNumberId: number;
	phoneE164: string;
	/** PD-14262 — softphone_number.status; badge a pending_port number (assigned
	 *  but not callable until the port completes). */
	status?: string;
	/** PD-14264 — ISO timestamp of port cutover (pending_port -> active), or null
	 *  if never ported. Drives the "place a test call" prompt for ~7 days after. */
	activatedAt?: string | null;
	voicemailGreetingUrl: string | null;
	isDefaultOutbound: boolean;
	/** Missed-call handling for this line. Reflects the stored kind (an
	 *  admin may have configured a forwarding kind, surfaced read-only). */
	fallbackKind: SoftphoneFallbackKindModel;
	/** The AI agent that picks up when fallbackKind === 'aiAgent'. */
	fallbackAiAgentId: number | null;
}

export type InboxScope = 'me' | 'group' | 'role';

/**
 * One row in the unified Communications Inbox (DD.7). Trimmed for
 * the inbox table; full call detail (transfer history, AI summary,
 * etc.) loads on row-expand via existing endpoints.
 */
export interface InboxCallSummaryModel {
	callId: number;
	externalId: string | null;
	createdDate: string;
	handledByUserId: number | null;
	handledByDisplayName: string | null;
	externalPartyPhone: string | null;
	externalPartyName: string | null;
	direction: 'inbound' | 'outbound' | null;
	phoneTo: string | null;
	result: string | null;
	isVoicemail: boolean;
	hasRecording: boolean;
	recordingDurationSeconds: number | null;
	dispositionCode: string | null;
	destinationGroupName: string | null;
	destinationRoleName: string | null;
}

export interface ListInboxCallsResponseModel {
	calls: ReadonlyArray<InboxCallSummaryModel>;
	totalCount: number;
}

/**
 * Browser client for the per-rep self-service softphone surface
 * (DD.4.H — `/api/softphone/me/*`). Lets a logged-in rep manage
 * their own voicemail greeting on each of their assigned numbers
 * without needing the `phone:system:configure` admin permission.
 *
 * Distinct from `SoftphonePhoneSystemQueryService`, which targets
 * the tenant-wide admin Communications screen — this service is
 * called from the rep's personal "My Phone" settings surface and
 * never asks the server to look up another user's data.
 */
export class SoftphoneSelfServiceQueryService {
	public static async listMyNumbers(): Promise<{
		numbers: ReadonlyArray<MyNumberSummaryModel>;
	}> {
		return ApiService.apiGet<{
			numbers: ReadonlyArray<MyNumberSummaryModel>;
		}>('/softphone/me/numbers');
	}

	public static async setMyVoicemailGreeting(
		softphoneNumberId: number,
		voicemailGreetingUrl: string | null,
	): Promise<{ success: true }> {
		return ApiService.apiPut<{ success: true }>(
			`/softphone/me/numbers/${softphoneNumberId}/voicemail-greeting`,
			{ voicemailGreetingUrl },
		);
	}

	/**
	 * Set the calling rep's missed-call handling on one of their numbers:
	 * 'none' (caller-not-available prompt, no voicemail), 'voicemail', or
	 * 'aiAgent' (with an agent id). Forwarding kinds stay admin-only.
	 */
	public static async setMyFallbackConfig(
		softphoneNumberId: number,
		fallbackKind: SelfServiceFallbackKind,
		fallbackAiAgentId: number | null,
	): Promise<{ success: true }> {
		return ApiService.apiPut<{ success: true }>(
			`/softphone/me/numbers/${softphoneNumberId}/fallback-config`,
			{ fallbackKind, fallbackAiAgentId },
		);
	}

	/**
	 * Mark one of the calling rep's assigned numbers as their default
	 * outbound caller-ID (DD.9.1). Pass `softphoneNumberId = null` to
	 * clear the default.
	 */
	public static async setMyDefaultOutboundNumber(
		softphoneNumberId: number | null,
	): Promise<{ success: true }> {
		return ApiService.apiPut<{ success: true }>(
			'/softphone/me/default-outbound-number',
			{ softphoneNumberId },
		);
	}

	/**
	 * List calls in the calling rep's Communications Inbox (DD.7).
	 *   - `scope: 'me'` — calls the rep handled.
	 *   - `scope: 'group'` — calls received on numbers assigned to
	 *     a Group the rep is a member of.
	 *   - `scope: 'role'` — same idea, for RBAC roles.
	 *
	 * Optional `voicemailOnly` filter shows only voicemail rows.
	 * Pagination: `limit` capped at 100 server-side.
	 */
	public static async listInboxCalls(args: {
		scope: InboxScope;
		voicemailOnly?: boolean;
		missedOnly?: boolean;
		/** Exclude calls the rep already acknowledged — drives the
		 *  unseen-missed-call count behind the red dot. */
		unseenOnly?: boolean;
		limit?: number;
		offset?: number;
	}): Promise<ListInboxCallsResponseModel> {
		const params = new URLSearchParams();
		params.append('scope', args.scope);
		if (args.voicemailOnly === true) params.append('voicemailOnly', 'true');
		if (args.missedOnly === true) params.append('missedOnly', 'true');
		if (args.unseenOnly === true) params.append('unseenOnly', 'true');
		if (args.limit != null) params.append('limit', String(args.limit));
		if (args.offset != null) params.append('offset', String(args.offset));
		return ApiService.apiGet<ListInboxCallsResponseModel>(
			`/softphone/me/inbox/calls?${params.toString()}`,
		);
	}

	/**
	 * Marks the calling rep's currently-unseen missed calls as seen,
	 * clearing the missed-call portion of the Communications red dot.
	 * Fired when the rep opens the Communications panel — the missed-call
	 * analogue of playing a voicemail. Idempotent.
	 */
	public static async markMissedCallsSeen(): Promise<{ success: boolean }> {
		return ApiService.apiPost<{ success: boolean }>(
			'/softphone/me/inbox/missed-calls/mark-seen',
			{},
		);
	}

	/**
	 * Returns the count of voicemails the calling rep is in scope to
	 * handle but has not personally listened to yet (DD.9.5.3).
	 * Powers the persistent red-dot indicator in the Communications
	 * Panel — the count only drops when the rep actually plays the
	 * recording, NOT when they dismiss a transient bell notification.
	 */
	public static async getUnlistenedVoicemailCount(
		scope: InboxScope,
	): Promise<{ count: number; scope: InboxScope }> {
		return ApiService.apiGet<{ count: number; scope: InboxScope }>(
			`/softphone/me/inbox/unlistened-voicemail-count?scope=${encodeURIComponent(scope)}`,
		);
	}

	/**
	 * Upload a voicemail-greeting audio file to the public CDN and
	 * return the URL. Caller persists the URL via
	 * {@link setMyVoicemailGreeting}. Server validates MIME (WAV /
	 * MP3 / M4A) and caps size at 5 MB.
	 */
	public static async uploadAudio(file: File): Promise<string> {
		const formData = new FormData();
		formData.append('file', file);
		const result = await ApiService.apiPost<{ success: true; url: string }>(
			'/softphone/me/audio',
			formData,
		);
		return result.url;
	}

	// ─────────────────────────────────────────────────────────────────
	// After-Call Work (ACW) — Phase 5 wrap-up timer
	// ─────────────────────────────────────────────────────────────────

	/**
	 * Get the calling rep's current ACW state. Used by the wrap-up
	 * panel's countdown chip to decide whether to render itself and
	 * to anchor the local clock to the carrier-side expiry timestamp.
	 */
	public static async getAcwState(): Promise<{
		inAcw: boolean;
		acwExpiresAt: string | null;
		acwDurationSeconds: number;
	}> {
		return ApiService.apiGet<{
			inAcw: boolean;
			acwExpiresAt: string | null;
			acwDurationSeconds: number;
		}>('/softphone/me/wrap-up/acw-state');
	}

	/**
	 * End the rep's ACW window immediately and restore their pre-call
	 * presence (so they're ringable again). Fired by:
	 *   - The rep clicking the "Resume calls" chip in the wrap-up panel.
	 *   - The chip's local countdown reaching zero (auto-fire so the
	 *     rep doesn't have to wait for the backend janitor sweep).
	 *
	 * Idempotent: server returns `{ restored: false }` when the rep
	 * isn't currently in ACW.
	 */
	public static async endAcw(): Promise<{
		success: true;
		restored: boolean;
	}> {
		return ApiService.apiPost<{ success: true; restored: boolean }>(
			'/softphone/me/wrap-up/end-acw',
			{},
		);
	}

	// ─────────────────────────────────────────────────────────────────
	// E911 (RAY BAUM §506) — emergency address
	// ─────────────────────────────────────────────────────────────────

	/**
	 * Read the calling rep's E911 emergency-address status. Drives the
	 * dial-time pre-flight check (the dialer hits this BEFORE Twilio
	 * for non-emergency outbound calls so it can surface the re-
	 * attestation modal instead of letting the backend reject) and
	 * the "Your Emergency Address" settings panel (commit 3).
	 */
	public static async getE911Status(): Promise<{
		hasAddress: boolean;
		isAttestationExpired: boolean;
		expiresAt: string | null;
		address: {
			friendlyName: string | null;
			street1: string;
			street2: string | null;
			city: string;
			region: string;
			postalCode: string;
			isoCountry: string;
		} | null;
	}> {
		return ApiService.apiGet<{
			hasAddress: boolean;
			isAttestationExpired: boolean;
			expiresAt: string | null;
			address: {
				friendlyName: string | null;
				street1: string;
				street2: string | null;
				city: string;
				region: string;
				postalCode: string;
				isoCountry: string;
			} | null;
		}>('/softphone/me/e911/status');
	}

	/**
	 * "Yes, this is still where I am" — bumps the rep's
	 * `lastAttestedAt` to now without re-running USPS validation.
	 * Fired by the re-attestation modal's [Confirm] button when the
	 * rep hasn't actually moved. For a real move, the wizard
	 * (commit 3) calls a separate `upsertAddress` endpoint.
	 */
	public static async attestE911(): Promise<{
		success: true;
		lastAttestedAt: string;
	}> {
		return ApiService.apiPost<{ success: true; lastAttestedAt: string }>(
			'/softphone/me/e911/attest',
			{},
		);
	}

	/**
	 * Set or update the rep's E911 emergency address. Server round-
	 * trips through Twilio's USPS validation; throws on a bad address
	 * with a user-readable message the wizard surfaces inline.
	 */
	public static async setE911Address(input: {
		friendlyName?: string | null;
		street1: string;
		street2?: string | null;
		city: string;
		region: string;
		postalCode: string;
		isoCountry?: string;
	}): Promise<{
		success: true;
		address: {
			friendlyName: string | null;
			street1: string;
			street2: string | null;
			city: string;
			region: string;
			postalCode: string;
			isoCountry: string;
		};
		validatedAt: string;
	}> {
		return ApiService.apiPost<{
			success: true;
			address: {
				friendlyName: string | null;
				street1: string;
				street2: string | null;
				city: string;
				region: string;
				postalCode: string;
				isoCountry: string;
			};
			validatedAt: string;
		}>('/softphone/me/e911/address', input);
	}
}
